import { useContext, useEffect, useState } from "react";
import AuthContext from "../Context/AuthProvider";
import {
  addPrescription,
  getPrescription,
  removePrescription,
  updatePrescription,
  viewPrescriptionById,
} from "../API/PrescriptionApi";
import { Toast } from "bootstrap";
import { getServiceDetails } from "../API/LinkedServicesApi";
import { Button, Card, Grid2, Tooltip, Typography, Box, CircularProgress } from "@mui/material";
import { useSnackbar } from "../Utility/UserMessageContext";
import PrescriptionView from "./PrescriptionView"; // Import the PrescriptionView component
import { formatAge, showLoadingImage } from "../Utility/Utility";

function PrescriptionForm({ serviceId, mode, prescriptionId, onRemovePrescription, onSavePrescription }) {
  const [errorMessage, setErrorMessage] = useState();
  const { auth } = useContext(AuthContext);
  const [prescriptionStatus, setPrescriptionStatus] = useState();
  const [currentService, setCurrentService] = useState();
  const [medicines, setMedicines] = useState([]);
  const [tests, setTests] = useState([]);
  const [complaint, setComplaint] = useState();
  const [diagnosis, setDiagnosis] = useState();
  const [additionalNotes, setAdditionalNotes] = useState();
  const [followupDate, setFollowupDate] = useState();
  const [prescriptionToBeEdited, setPrescriptionToBeEdited] = useState();
  const [currentPrescription, setCurrentPrescription] = useState();
  const {showSnackbar} = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadService = async () => {
      if (auth != null && serviceId != null) {
        setIsSaving(true); // Set loading state when fetching service details
        try {
          var result = await getServiceDetails(auth, serviceId);
          if (result.isSuccess) {
            setCurrentService(result?.data);
          }
        } catch (error) {
          showSnackbar("Error loading service details: " + error.message, "error");
        } finally {
          // Only set loading to false if we're not also loading the prescription
          if (prescriptionId == null) {
            setIsSaving(false);
          }
        }
      }
    };

    const loadPrescription = async () => {
      if (auth != null && prescriptionId != null) {
        setIsSaving(true);
        try {
          var result = await getPrescription(auth, prescriptionId);
          if (result.isSuccess) {
            var prescription = result?.data;
            setPrescriptionToBeEdited(prescription);
            setPrescriptionStatus(prescription?.status);
            var detailedPrescriptionJson = JSON.parse(
              prescription?.detailedPrescription
            );
            if (prescription?.detailedPrescription) {
              setComplaint(detailedPrescriptionJson?.complaint);
              setDiagnosis(detailedPrescriptionJson?.diagnosis);
              setMedicines(detailedPrescriptionJson?.medicines);
              setTests(detailedPrescriptionJson?.tests);
              setAdditionalNotes(detailedPrescriptionJson?.additionalNotes);
              setFollowupDate(detailedPrescriptionJson?.followupDate);
            } else {
              setComplaint("");
              setDiagnosis("");
              setMedicines([]);
              setTests([]);
              setAdditionalNotes("");
              setFollowupDate("");
            }
          } else {
            showSnackbar(result?.data, "error");
          }
        } catch (error) {
          showSnackbar("Error loading prescription: " + error.message, "error");
        } finally {
          setIsSaving(false);
        }
      }
    };

    if (prescriptionId) {
      loadPrescription();
      loadService();
    } else {
      loadService();
    }
  }, [auth, serviceId, prescriptionId, setCurrentPrescription]);

  const savePrescription = async (status) => {
    setIsSaving(true);
    try {
      var prescription = {};

      prescription.complaint = complaint;
      prescription.diagnosis = diagnosis;
      prescription.medicines = medicines;
      prescription.tests = tests;
      prescription.additionalNotes = additionalNotes;
      prescription.followupDate = followupDate;
      prescription.status = status;
      var result = {};
      if (!prescriptionToBeEdited) {
        result = await addPrescription(auth, prescription, currentService?.id, "General2");
      } else {
        result = await updatePrescription(
          auth,
          prescription,
          prescriptionToBeEdited?.id
        );
      }
      if (result.isSuccess) {
        onSavePrescription(serviceId, prescriptionId, prescription.status);
        showSnackbar("The prescription has been saved successfully.", "success");
      } else {
        showSnackbar(result?.data, "error");
      }
    } catch (error) {
      showSnackbar("Error saving prescription: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const onRemovePrescriptionClick = async () => {
    setIsSaving(true);
    try {
      if (auth != null && prescriptionId != null) {
        var result = await removePrescription(auth, prescriptionId);
        if (result?.isSuccess) {
          onRemovePrescription(serviceId, prescriptionId);
        } else {
          showSnackbar(result?.data, "error");
        }
      }
    } catch (error) {
      showSnackbar("Error removing prescription: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const viewPrescription = async () => {
    setIsSaving(true);
    try {
      if (auth != null && prescriptionId != null) {
        await viewPrescriptionById(auth, prescriptionId);
      }
    } catch (error) {
      showSnackbar("Error viewing prescription: " + error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Function to render form for editable mode
  const renderEditableForm = () => {
    return (
      <Card
        variant="outlined"
        fullWidth
        sx={{ width: "100%", padding: "10px", opacity: isSaving ? 0.7 : 1, transition: 'opacity 0.3s ease' }}
      >
        <Grid2 container size={12}>
          <Grid2 size={4}>
            <Grid2 size={12}>
              <Typography>Patient Name</Typography>
            </Grid2>
            <Grid2 size={12}>
              <Typography>
                {currentService?.patientPrefix +
                  " " +
                  currentService?.patientFirstName +
                  " " +
                  currentService?.patientLastName}
              </Typography>
            </Grid2>
          </Grid2>
          <Grid2 size={4}>
            <Grid2 size={12}>
              <Typography>Age and Gender</Typography>
            </Grid2>
            <Grid2 size={12}>
              <Typography>
               {formatAge(currentService?.patientAgeInYears)} and{" "}
                                          {currentService?.patientGender}
              </Typography>
            </Grid2>
          </Grid2>
          <Grid2 size={4}>
            <Grid2 size={12}>
              <Typography>MRN</Typography>
            </Grid2>
            <Grid2 size={12}>
              <Typography>{currentService?.patientMRN}</Typography>
            </Grid2>
          </Grid2>
        </Grid2>
        
        <Grid2 size={12}>
          <Grid2 size={4}>
            <div class="row">
              <label class="fw-bold">Visit Id</label>
            </div>
            <div class="row d-flex">
              <label>{currentService?.visitDisplayId}</label>
            </div>
          </Grid2>
          <Grid2 size={4}></Grid2>
          <Grid2 size={4}>
            <div class="row d-flex">
              <label class="fw-bold">Date</label>
            </div>
            <div class="row d-flex">
              <label>
                {new Date().toLocaleString("en-US", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </label>
            </div>
          </Grid2>
        </Grid2>
        
        {/* Patient's Complaint Section */}
        <div class="row justify-content-center align-items-center pt-3 p-0">
          <label class="fw-bold">Patient's Complaint</label>
        </div>
        <div class="row justify-content-center align-items-center px-2">
          <textarea
            class="span6 w-100 d-flex border rounded"
            rows="5"
            placeholder="Write about patient's complaint."
            value={complaint}
            onChange={(e) => {
              setComplaint(e.target.value);
            }}
          ></textarea>
        </div>
        
        {/* Diagnosis Notes Section */}
        <div class="row justify-content-center align-items-center pt-3 p-0">
          <label class="fw-bold">Diagnosis Notes</label>
        </div>
        <div class="row justify-content-center align-items-center p-2">
          <textarea
            class="span6 w-100 d-flex border rounded"
            rows="5"
            placeholder="Write about your diagnosis."
            value={diagnosis}
            onChange={(e) => {
              setDiagnosis(e.target.value);
            }}
          ></textarea>
        </div>

        {/* Medicines Section */}
        <div class="row justify-content-center align-items-center pt-3 p-0">
          <div class="col">
            <label class="fw-bold">Medicines</label>
          </div>

          <div class="col d-flex justify-content-end">
            <button
              class="btn btn-outline-primary btn-sm"
              onClick={() => {
                setMedicines([
                  ...medicines,
                  { serialNo: medicines.length + 1 },
                ]);
              }}
            >
              <i class="bi bi-plus-lg"></i>
            </button>
          </div>
        </div>

        <div class="row justify-content-center align-items-center p-2">
          <table class="table border-0" id="medicineList">
            <thead>
              <tr>
                <th scope="col" class="col-1 border-0">#</th>
                <th scope="col" class="col-3 border-0">Medicine</th>
                <th scope="col" class="col-2 border-0">Duration</th>
                <th scope="col" class="col-2 border-0">Frequency</th>
                <th scope="col" class="col-3 border-0">Instructions</th>
                <th scope="col" class="col-1 border-0">Actions</th>
              </tr>
            </thead>
            <tbody>
              {medicines &&
                medicines.map((med) => (
                  <tr key={med.serialNo}>
                    <th class="border-0" scope="row">
                      {med.serialNo}
                    </th>
                    <td class="border-0">
                      <input
                        class="w-100"
                        type="text"
                        value={med.name}
                        onChange={(e) => {
                          var copiedMedicines = [...medicines];
                          var medIndexToBeUpdated =
                            copiedMedicines.findIndex(
                              (m) => m.serialNo === med.serialNo
                            );
                          copiedMedicines[medIndexToBeUpdated].name =
                            e.target.value;
                          setMedicines(copiedMedicines);
                        }}
                      ></input>
                    </td>
                    <td class="border-0">
                      <input
                        class="w-100"
                        type="text"
                        value={med.duration}
                        onChange={(e) => {
                          var copiedMedicines = [...medicines];
                          var medIndexToBeUpdated =
                            copiedMedicines.findIndex(
                              (m) => m.serialNo === med.serialNo
                            );
                          copiedMedicines[medIndexToBeUpdated].duration =
                            e.target.value;
                          setMedicines(copiedMedicines);
                        }}
                      ></input>
                    </td>
                    <td class="border-0">
                      <input
                        class="w-100"
                        type="text"
                        value={med.frequency}
                        onChange={(e) => {
                          var copiedMedicines = [...medicines];
                          var medIndexToBeUpdated =
                            copiedMedicines.findIndex(
                              (m) => m.serialNo === med.serialNo
                            );
                          copiedMedicines[medIndexToBeUpdated].frequency =
                            e.target.value;
                          setMedicines(copiedMedicines);
                        }}
                      ></input>
                    </td>
                    <td class="border-0">
                      <textarea
                        class="w-100"
                        rows={2}
                        type="text"
                        value={med.instructions}
                        onChange={(e) => {
                          var copiedMedicines = [...medicines];
                          var medIndexToBeUpdated =
                            copiedMedicines.findIndex(
                              (m) => m.serialNo === med.serialNo
                            );
                          copiedMedicines[
                            medIndexToBeUpdated
                          ].instructions = e.target.value;
                          setMedicines(copiedMedicines);
                        }}
                      ></textarea>
                    </td>

                    <td class="border-0">
                      <button
                        class="btn btn-outline-danger small p-1 ms-1"
                        onClick={() => {
                          const filteredMeds = medicines.filter(
                            (m) => m.serialNo !== med.serialNo
                          );

                          var count = 0;
                          const nextMeds = filteredMeds.map((m) => {
                            return { ...m, serialNo: ++count };
                          });
                          setMedicines(nextMeds);
                        }}
                      >
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        
        {/* Recommended Tests Section */}
        <div class="row justify-content-center align-items-center pt-2">
          <div class="col">
            <label class="fw-bold">Recommended Tests</label>
          </div>

          <div class="col d-flex justify-content-end">
            <button
              class="btn btn-outline-primary btn-sm"
              onClick={() => {
                setTests([...tests, { serialNo: tests.length + 1 }]);
              }}
            >
              <i class="bi bi-plus-lg"></i>
            </button>
          </div>
        </div>
        
        <div class="row justify-content-center align-items-center p-2 ">
          <table class="table border-0">
            <thead>
              <tr>
                <th scope="col" class="col-1 border-0">#</th>
                <th scope="col" class="col-3 border-0">Test Name</th>
                <th scope="col" class="col-3 border-0">Tentative Date of Test</th>
                <th scope="col" class="col-4 border-0">Instructions</th>
                <th scope="col" class="col-1 border-0">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tests &&
                tests.map((test) => (
                  <tr key={test.serialNo}>
                    <th scope="row" class="border-0">
                      {test.serialNo}
                    </th>
                    <td class="border-0">
                      <input
                        class="w-100"
                        type="text"
                        value={test.name}
                        onChange={(e) => {
                          var copiedTests = [...tests];
                          var testIndexToBeUpdated =
                            copiedTests.findIndex(
                              (t) => t.serialNo === test.serialNo
                            );
                          copiedTests[testIndexToBeUpdated].name =
                            e.target.value;
                          setTests(copiedTests);
                        }}
                      ></input>
                    </td>
                    <td class="border-0">
                      <input
                        class="w-100"
                        type="date"
                        value={test.toBeDoneOn}
                        onChange={(e) => {
                          var copiedTests = [...tests];
                          var testIndexToBeUpdated =
                            copiedTests.findIndex(
                              (t) => t.serialNo === test.serialNo
                            );
                          copiedTests[testIndexToBeUpdated].toBeDoneOn =
                            e.target.value;
                          setTests(copiedTests);
                        }}
                      ></input>
                    </td>
                    <td class="border-0">
                      <textarea
                        class="w-100"
                        rows={2}
                        type="text"
                        value={test.instructions}
                        onChange={(e) => {
                          var copiedTests = [...tests];
                          var testIndexToBeUpdated =
                            copiedTests.findIndex(
                              (t) => t.serialNo === test.serialNo
                            );
                          copiedTests[testIndexToBeUpdated].instructions =
                            e.target.value;
                          setTests(copiedTests);
                        }}
                      ></textarea>
                    </td>
                    <td class="border-0">
                      <button
                        class="btn btn-outline-danger small p-1 ms-1"
                        onClick={() => {
                          const filteredTests = tests.filter(
                            (t) => t.serialNo !== test.serialNo
                          );

                          var count = 0;
                          const nextTests = filteredTests.map((t) => {
                            return { ...t, serialNo: ++count };
                          });
                          setTests(nextTests);
                        }}
                      >
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        
        {/* Additional Notes Section */}
        <div class="row justify-content-center align-items-center mt-4 p-1">
          <h5>Additional Notes</h5>
        </div>
        <div class="row justify-content-center align-items-center p-1">
          <textarea
            class="span6 w-100 d-flex border rounded"
            rows="5"
            value={additionalNotes}
            onChange={(e) => {
              setAdditionalNotes(e.target.value);
            }}
          ></textarea>
        </div>
        
        {/* Follow Up Date Section */}
        <div class="row align-items-center pb-2 pt-2">
          <div class="col-2">
            <h5>Follow Up Date</h5>
          </div>
          <div class="col-4">
            <input
              class="form-control border"
              type="date"
              value={followupDate}
              onChange={(e) => {
                setFollowupDate(e.target.value);
              }}
            ></input>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Loading Overlay */}
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(216, 214, 214, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1301, // One level above Dialog
          }}
        >
          {showLoadingImage()}
        </div>
      )}

      <Grid2 container size={12} sx={{ marginX: "10px", marginY: "10px" }}>
        <Grid2 container size={12}>
          <Grid2 container size={4} spacing={1} alignContent="center">
            {prescriptionStatus === "Draft" && (
              <>
                <Tooltip title="Delete Prescription">
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={onRemovePrescriptionClick}
                    disabled={isSaving}
                  >
                    Delete
                  </Button>
                </Tooltip>
              </>
            )}
            {prescriptionStatus === "Final" && (
              <Tooltip title="Download as PDF">
                <Button
                  variant="contained"
                  size="small"
                  color="secondary"
                  onClick={viewPrescription}
                  disabled={isSaving}
                >
                  Download
                </Button>
              </Tooltip>
            )}
            {prescriptionStatus !== "Final" && (
              <>
                <Tooltip title="Save Prescription as Draft">
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={() => savePrescription("Draft")}
                    disabled={isSaving}
                  >
                    Save
                  </Button>
                </Tooltip>
                <Tooltip title="Finalize Prescription">
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={() => savePrescription("Final")}
                    disabled={isSaving}
                  >
                    Finalize
                  </Button>
                </Tooltip>
              </>
            )}
          </Grid2>
        </Grid2>

        <Grid2 container size={12}>
          <Grid2 size={9} sx={{ paddingTop: "10px" }}>
            {prescriptionStatus === "Final" ? (
              // Use the PrescriptionView component for read-only mode
              <Box
                sx={{
                  opacity: isSaving ? 0.7 : 1,
                  transition: "opacity 0.3s ease",
                }}
              >
                <PrescriptionView
                  currentService={currentService}
                  complaint={complaint}
                  diagnosis={diagnosis}
                  medicines={medicines}
                  tests={tests}
                  additionalNotes={additionalNotes}
                  followupDate={followupDate}
                />
              </Box>
            ) : (
              // Use the editable form for draft mode
              renderEditableForm()
            )}
          </Grid2>
          <Grid2 size={3}></Grid2>
        </Grid2>
      </Grid2>
    </Box>
  );
}

export default PrescriptionForm;